import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  DollarSign, Search, Filter, ChevronDown, ChevronUp,
  Calendar, Download, FileText, CreditCard, Building2,
  ChevronLeft, ChevronRight, Menu, TrendingUp, AlertCircle,
  Receipt, Clock, ArrowUpRight, ArrowDownRight, CheckCircle,
  Wallet, Banknote, PieChart, TruckIcon, Package, Eye, Upload, Users, X, MapPin, XCircle, Loader2
} from 'lucide-react';
import SideNavigation from '../components/SideNavigation';
import useScreenSize from '../hooks/useScreenSize';
import api from '../utils/api';
import { fetchNotifications } from '../store/slices/notificationSlice';
import { useBilling } from '../hooks/useBilling';
import BillingTimeline from '../components/BillingTimeline';
import { createPortal } from 'react-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import DocumentCard from '../components/DocumentCard';
import { useDropzone } from 'react-dropzone';
import AddChargeModal from '../components/AddChargeModal';
import { selectIsBroker } from '../store/slices/authSlice';
import MobileInvoiceItem from '../components/MobileInvoiceItem';
import DisputeModal from '../components/DisputeModal';
import { format, isAfter, formatDistanceToNow } from 'date-fns';
import { useDebounce } from '../hooks/useDebounce';
import { getLoadDisplayId } from '../utils/loadUtils';

const STATUS_CONFIG = {
  load_completed: {
    icon: CheckCircle,
    label: 'Load Completed',
    color: 'gray'
  },
  invoice_uploaded: {
    icon: FileText,
    label: 'Invoice Uploaded',
    color: 'blue'
  },
  charges_approved: {
    icon: CheckCircle,
    label: 'Charges Approved',
    color: 'green'
  },
  charges_disputed: {
    icon: AlertCircle,
    label: 'Disputed',
    color: 'red'
  },
  under_review: {
    icon: Clock,
    label: 'Under Review',
    color: 'yellow'
  },
  awaiting_payment: {
    icon: Clock,
    label: 'Awaiting Payment',
    color: 'yellow'
  },
  payment_marked: {  // This was missing!
    icon: DollarSign,
    label: 'Payment Marked',
    color: 'green'
  },
  payment_verified: {
    icon: CheckCircle,
    label: 'Payment Verified',
    color: 'green'
  },
  released_to_carrier: {
    icon: TruckIcon,
    label: 'Released',
    color: 'green'
  },
  dispute_resolved: {
    icon: CheckCircle,
    label: 'Resolved',
    color: 'green'
  }
};

const TABLE_COLUMNS = [
  { 
    id: 'loadId', 
    header: 'Reference', 
    sortable: true,
    minWidth: '140px',
    render: (invoice) => (
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center flex-shrink-0 border border-slate-200">
          <Receipt className="h-4 w-4 text-slate-500" aria-hidden="true" />
        </div>
        <div className="min-w-0">
          <div className="font-semibold text-slate-900 text-sm">{getLoadDisplayId(invoice)}</div>
          {invoice.transaction_uuid ? (
            <div className="flex items-center gap-1">
              <span className="font-mono text-[10px] font-medium tracking-wider text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-100">
                {invoice.transaction_uuid.substring(0, 8).toUpperCase()}
              </span>
            </div>
          ) : (
            <span className="text-[10px] text-slate-400 font-medium">No Transaction</span>
          )}
        </div>
      </div>
    )
  },
  { 
    id: 'route', 
    header: 'Route', 
    sortable: false,
    minWidth: '180px',
    render: (invoice) => (
      <div className="flex items-center gap-2">
        <div className="flex flex-col items-center gap-0.5">
          <div className="w-2 h-2 rounded-full bg-blue-500"></div>
          <div className="w-0.5 h-3 bg-gradient-to-b from-blue-500 to-emerald-500"></div>
          <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
        </div>
        <div className="min-w-0 text-sm">
          <div className="font-medium text-slate-900 truncate">
            {invoice.origin_city}, {invoice.origin_state}
          </div>
          <div className="text-slate-500 truncate">
            {invoice.destination_city}, {invoice.destination_state}
          </div>
        </div>
      </div>
    )
  },
  { 
    id: 'customer', 
    header: 'Customer', 
    sortable: true,
    minWidth: '140px',
    render: (invoice) => (
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-100 to-purple-50 flex items-center justify-center flex-shrink-0 border border-violet-100">
          <span className="text-xs font-bold text-violet-600">
            {invoice.customer_name?.charAt(0)?.toUpperCase() || 'C'}
          </span>
        </div>
        <div className="min-w-0">
          <div className="font-medium text-slate-900 text-sm truncate max-w-[120px]">{invoice.customer_name}</div>
        </div>
      </div>
    )
  },
  { 
    id: 'amount', 
    header: 'Amount', 
    sortable: true,
    minWidth: '100px',
    align: 'center',
    render: (invoice) => (
      <div className="text-center">
        <div className="font-semibold text-slate-900 text-sm">
          {formatCurrency(invoice.final_rate)}
        </div>
        {invoice.base_rate !== invoice.final_rate && (
          <div className="text-[10px] text-slate-400 line-through">
            {formatCurrency(invoice.base_rate)}
          </div>
        )}
      </div>
    )
  },
  { 
    id: 'dueDate', 
    header: 'Due', 
    sortable: true,
    minWidth: '100px',
    render: (invoice) => {
      const isOverdue = !isAfter(new Date(invoice.dueDate), new Date());
      return (
        <div className="text-sm">
          <div className={`font-medium ${isOverdue ? 'text-red-600' : 'text-slate-900'}`}>
            {format(new Date(invoice.dueDate), 'MMM dd')}
          </div>
          <div className={`text-[10px] ${isOverdue ? 'text-red-500' : 'text-slate-400'}`}>
            {isAfter(new Date(invoice.dueDate), new Date()) 
              ? formatDistanceToNow(new Date(invoice.dueDate), { addSuffix: false })
              : 'Overdue'
            }
          </div>
        </div>
      );
    }
  },
  { 
    id: 'status', 
    header: 'Status', 
    sortable: true,
    minWidth: '130px',
    render: (invoice) => <StatusBadge status={invoice.status} />
  },
  { 
    id: 'actions', 
    header: '', 
    sortable: false,
    minWidth: '80px',
    render: (invoice, { onExpand, isExpanded, onAddCharge, canAddCharges }) => (
      <div className="flex items-center justify-end gap-1">
        {canAddCharges && (
          <button
            onClick={() => onAddCharge(invoice)}
            className="p-1.5 rounded-lg text-slate-400 hover:text-emerald-600 
              hover:bg-emerald-50 transition-colors duration-200"
            title="Add Additional Charge"
          >
            <DollarSign className="w-4 h-4" />
          </button>
        )}
        <button
          onClick={onExpand}
          className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 
            hover:bg-slate-100 transition-colors duration-200"
          title={isExpanded ? "Show less" : "Show more"}
        >
          {isExpanded ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </button>
      </div>
    )
  }
];

const DOCUMENT_TYPES = {
  carrier: {
    options: [
      { value: 'insurance_certificate', label: 'Insurance Certificate' },
      { value: 'operating_authority', label: 'Operating Authority' },
      { value: 'w9_form', label: 'W9 Form' }
    ]
  },
  customer: {
    options: [
      { value: 'credit_application', label: 'Credit Application' },
      { value: 'payment_authorization', label: 'Payment Authorization' },
      { value: 'company_profile', label: 'Company Profile' }
    ]
  },
  load: {
    options: [
      { value: 'bill_of_lading', label: 'Bill of Lading' },
      { value: 'proof_of_delivery', label: 'Proof of Delivery' },
      { value: 'rate_confirmation', label: 'Rate Confirmation' },
      { value: 'lumper_receipt', label: 'Lumper Receipt' }
    ]
  },
  billing: {
    options: [
      { value: 'invoice', label: 'Invoice' }
    ]
  },
  dispute: {
    options: [
      { value: 'rate_agreement', label: 'Rate Agreement' },
      { value: 'damage_report', label: 'Damage Report' },
      { value: 'detention_log', label: 'Detention Log' },
      { value: 'communication_record', label: 'Communication Record' },
      { value: 'supporting_document', label: 'Supporting Document' }
    ]
  }
};

const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.load_completed;
  const Icon = config.icon;
  
  const colorVariants = {
    green: 'bg-emerald-50 text-emerald-700 border-emerald-200/50',
    yellow: 'bg-amber-50 text-amber-700 border-amber-200/50',
    red: 'bg-red-50 text-red-700 border-red-200/50',
    blue: 'bg-blue-50 text-blue-700 border-blue-200/50',
    gray: 'bg-slate-50 text-slate-600 border-slate-200/50'
  };

  return (
    <div className={`
      inline-flex items-center gap-1 px-2 py-1
      rounded-md text-xs font-medium
      border whitespace-nowrap
      ${colorVariants[config.color] || colorVariants.gray}
    `}>
      <Icon className="w-3 h-3 flex-shrink-0" />
      <span className="truncate max-w-[80px]">{config.label}</span>
    </div>
  );
};

// Helper function to format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

const MissingDocumentsAlert = ({ onUpload }) => (
  <div className="border-2 border-amber-200 bg-amber-50 rounded-lg p-4 mb-4">
    <div className="flex items-start">
      <div className="flex-shrink-0">
        <AlertCircle className="h-5 w-5 text-amber-400" aria-hidden="true" />
      </div>
      <div className="ml-3 flex-1">
        <h3 className="text-sm font-medium text-amber-800">
          Required Documents Missing
        </h3>
        <div className="mt-2 text-sm text-amber-700">
          <p>
            Please upload supporting documentation for your dispute. This is required to process your review.
          </p>
        </div>
        <div className="mt-4">
          <div className="-mx-2 -my-1.5 flex">
            <button
              type="button"
              onClick={onUpload}
              className="rounded-md bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-800 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-600 focus:ring-offset-2 focus:ring-offset-amber-50"
            >
              Upload Documents
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Add new date range options
const DATE_RANGE_OPTIONS = [
  { value: 'last5', label: 'Last 5 Days' },
  { value: 'last30', label: 'Last 30 Days' },
  { value: 'last90', label: 'Last 90 Days' },
  { value: 'thisYear', label: 'This Year' },
  { value: 'lastYear', label: 'Last Year' },
  { value: 'custom', label: 'Custom Range' }
];

const FilterBar = ({ 
  activeStatuses,
  onStatusChange,
  dateRange,
  onDateRangeChange,
  searchTerm,
  onSearchChange,
  metrics,
  customDateRange,
  onCustomDateChange,
  isSearching
}) => {
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  
  // Group statuses by category for better organization
  const statusGroups = {
    'Processing': [
      'load_completed',
      'invoice_uploaded',
      'charges_approved'
    ],
    'Payment': [
      'awaiting_payment',
      'payment_marked',
      'payment_verified',
      'released_to_carrier'
    ],
    'Issues': [
      'charges_disputed',
      'under_review',
      'dispute_resolved'
    ]
  };

  // Handle status button click
  const handleStatusClick = (status) => {
    const isActive = activeStatuses.includes(status);
    
    if (isActive) {
      // Remove the status
      onStatusChange(activeStatuses.filter(s => s !== status));
      return;
    }

    // If trying to add a new status
    if (activeStatuses.length === 0) {
      // First status - allow any
      onStatusChange([status]);
    } else if (activeStatuses.length === 1) {
      // Second status - only allow if one is 'charges_disputed'
      const currentStatus = activeStatuses[0];
      if (currentStatus === 'charges_disputed' || status === 'charges_disputed') {
        onStatusChange([currentStatus, status]);
      } else {
        // Show toast or alert that only one non-dispute status can be selected
        toast.warning('Only one status can be selected at a time, unless combined with Disputed status');
      }
    } else {
      // Don't allow more than two statuses
      toast.warning('Maximum of two statuses can be selected when one is Disputed');
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-md p-6 mb-6">
      {/* Search and Date Range Row */}
      <div className="flex flex-col lg:flex-row gap-4 mb-6">
        <div className="flex-1">
          <div className="relative">
            <input
              type="text"
              placeholder="Search invoices, customers, or routes..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <div className="absolute left-3 top-2.5">
              {isSearching ? (
                <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
              ) : (
                <Search className="h-5 w-5 text-gray-400" />
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <select
            value={dateRange}
            onChange={(e) => {
              const value = e.target.value;
              onDateRangeChange(value);
              if (value === 'custom') {
                // Show custom date range picker
                setShowCustomDatePicker(true);
              }
            }}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            {DATE_RANGE_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {/* Custom Date Range Picker */}
          {dateRange === 'custom' && (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={customDateRange.start}
                onChange={(e) => onCustomDateChange({ ...customDateRange, start: e.target.value })}
                className="px-3 py-2 border border-gray-200 rounded-lg"
              />
              <span>to</span>
              <input
                type="date"
                value={customDateRange.end}
                onChange={(e) => onCustomDateChange({ ...customDateRange, end: e.target.value })}
                className="px-3 py-2 border border-gray-200 rounded-lg"
              />
            </div>
          )}
        </div>
      </div>

      {/* Status Filter Groups */}
      <div className="space-y-4">
        {Object.entries(statusGroups).map(([groupName, statuses]) => (
          <div key={groupName} className="space-y-2">
            <h3 className="text-sm font-medium text-gray-500">{groupName}</h3>
            <div className="flex flex-wrap gap-2">
              {statuses.map(status => {
                const config = STATUS_CONFIG[status];
                const isActive = activeStatuses.includes(status);
                return (
                  <button
                    key={status}
                    onClick={() => handleStatusClick(status)}
                    className={`
                      inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium
                      transition-all duration-200 gap-1.5
                      ${isActive
                        ? `bg-${config.color}-100 text-${config.color}-700 border border-${config.color}-200`
                        : 'bg-gray-50 text-gray-600 border border-gray-100 hover:bg-gray-100'
                      }
                      ${!isActive && activeStatuses.length === 2 ? 'opacity-50 cursor-not-allowed' : ''}
                      ${!isActive && activeStatuses.length === 1 && 
                        activeStatuses[0] !== 'charges_disputed' && 
                        status !== 'charges_disputed' ? 'opacity-50 cursor-not-allowed' : ''}
                    `}
                    disabled={
                      !isActive && 
                      ((activeStatuses.length === 2) || 
                       (activeStatuses.length === 1 && 
                        activeStatuses[0] !== 'charges_disputed' && 
                        status !== 'charges_disputed'))
                    }
                  >
                    <config.icon className="w-4 h-4" />
                    {config.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Active Filters Display */}
      {(activeStatuses.length > 0 || dateRange !== 'last30') && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-500">Active Filters:</span>
            {activeStatuses.map(status => (
              <div key={status} className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-md text-sm">
                {STATUS_CONFIG[status]?.label}
                <button
                  onClick={() => onStatusChange(activeStatuses.filter(s => s !== status))}
                  className="ml-1 hover:text-blue-900"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
            {dateRange !== 'last30' && (
              <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-md text-sm">
                {DATE_RANGE_OPTIONS.find(opt => opt.value === dateRange)?.label}
                <button
                  onClick={() => onDateRangeChange('last30')}
                  className="ml-1 hover:text-blue-900"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const TableHeader = ({ columns, sortBy, sortOrder, onSort }) => (
  <thead className="bg-slate-50/80 border-b border-slate-200">
    <tr>
      {columns.map(({ id, header, sortable, minWidth, align }) => (
        <th
          key={id}
          style={{ minWidth }}
          className={`px-3 py-3 text-[10px] font-semibold text-slate-500 uppercase tracking-wider ${
            align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : 'text-left'
          }`}
        >
          {sortable ? (
            <button
              onClick={() => onSort(id)}
              className="group inline-flex items-center gap-1 hover:text-slate-700 transition-colors"
            >
              <span>{header}</span>
              <span className="flex flex-col">
                <ChevronUp 
                  className={`w-3 h-3 ${
                    sortBy === id && sortOrder === 'asc' 
                      ? 'text-blue-500' 
                      : 'text-slate-300 group-hover:text-slate-400'
                  }`} 
                />
                <ChevronDown 
                  className={`w-3 h-3 -mt-1.5 ${
                    sortBy === id && sortOrder === 'desc' 
                      ? 'text-blue-500' 
                      : 'text-slate-300 group-hover:text-slate-400'
                  }`} 
                />
              </span>
            </button>
          ) : (
            header
          )}
        </th>
      ))}
    </tr>
  </thead>
);

const Billings = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const {
    billings = [],
    metrics,
    loading,
    error,
    pagination,
    fetchBillings,
    handleAction,
    getNextActions
  } = useBilling();

  const isCustomer = user.role.startsWith('customer');
  const [activeStatuses, setActiveStatuses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const { isMobile, isTablet, isDesktop } = useScreenSize();
  const [sidebarOpen, setSidebarOpen] = useState(isDesktop);
  const [dateRange, setDateRange] = useState('last30');
  const [customDateRange, setCustomDateRange] = useState({
    start: '',
    end: ''
  });

  const isBroker = useSelector(selectIsBroker);

  const [isSearching, setIsSearching] = useState(false);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Add currentParams object to track filter state
  const currentParams = {
    page: pagination.currentPage,
    status: activeStatuses.length > 0 ? activeStatuses.join(',') : 'all',
    dateRange: dateRange,
    search: debouncedSearchTerm,
    sortBy,
    sortOrder,
    ...(dateRange === 'custom' && {
      customDateStart: customDateRange.start,
      customDateEnd: customDateRange.end
    })
  };

  useEffect(() => {
    dispatch(fetchNotifications({ page: currentPage }));
  }, [dispatch, currentPage]);

  useEffect(() => {
    const fetchData = async () => {
      if (debouncedSearchTerm) {
        setIsSearching(true);
      }
      
      try {
        await fetchBillings(currentParams);
      } finally {
        setIsSearching(false);
      }
    };

    fetchData();
  }, [
    fetchBillings,
    pagination.currentPage,
    activeStatuses,
    dateRange,
    customDateRange,
    debouncedSearchTerm,
    sortBy,
    sortOrder
  ]);

  useEffect(() => {
    setSidebarOpen(isDesktop);
  }, [isDesktop]);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  const dashboardData = {
    totalBilled: formatCurrency(metrics.totalBilled),
    pendingAmount: formatCurrency(metrics.pendingAmount),
    paidLastMonth: formatCurrency(metrics.paidLastMonth),
    averagePaymentTime: metrics.averagePaymentTime
  };

  const handleSort = (columnId) => {
    if (sortBy === columnId) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(columnId);
      setSortOrder('asc');
    }
  };

  const handleDisputeCharges = async (invoiceId, disputeData) => {
    try {
      return await handleAction(invoiceId, 'charges_disputed', disputeData);
    } catch (error) {
      console.error('Error in handleDisputeCharges:', error);
      throw error;
    }
  };

  const handleRefreshBilling = async (invoiceId) => {
    await fetchBillings(currentParams);
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    // Reset to first page when searching
    if (pagination.currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden">
      <SideNavigation isOpen={sidebarOpen} onToggle={toggleSidebar} />
      
      <main className={`flex-1 relative overflow-hidden transition-all duration-300 ${
        isMobile || isTablet ? 'ml-0' : (sidebarOpen ? 'ml-64' : 'ml-16')
      }`}>
        {/* Scrollable container with padding */}
        <div className="absolute inset-0 overflow-y-auto">
          <div className="container mx-auto px-4 py-6 max-w-[1920px] min-h-full">
            {/* Header Section */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                {(isMobile || isTablet) && (
                  <button
                    onClick={toggleSidebar}
                    className="p-2 bg-white rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
                  >
                    <Menu className="h-5 w-5 text-gray-600" />
                  </button>
                )}
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                  Billings & Payments
                </h1>
              </div>
            </div>

            {/* Dashboard Cards - Responsive Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
              <DashboardCard
                title="Total Billed"
                value={dashboardData.totalBilled}
                icon={Wallet}
                trend="up"
                trendValue="+12.5%"
                color="blue"
              />
              <DashboardCard
                title="Pending Amount"
                value={dashboardData.pendingAmount}
                icon={Clock}
                color="yellow"
              />
              <DashboardCard
                title="Paid Last Month"
                value={dashboardData.paidLastMonth}
                icon={CheckCircle}
                trend="up"
                trendValue="+8.2%"
                color="green"
              />
              <DashboardCard
                title="Average Payment Time"
                value={dashboardData.averagePaymentTime}
                icon={Calendar}
                trend="down"
                trendValue="-2 days"
                color="purple"
              />
            </div>

            {/* Filter Section - More Compact */}
            <div className="bg-white rounded-xl shadow-sm mb-6">
              <div className="p-4 sm:p-6">
                <FilterBar
                  activeStatuses={activeStatuses}
                  onStatusChange={setActiveStatuses}
                  dateRange={dateRange}
                  onDateRangeChange={setDateRange}
                  searchTerm={searchTerm}
                  onSearchChange={handleSearchChange}
                  metrics={metrics}
                  customDateRange={customDateRange}
                  onCustomDateChange={setCustomDateRange}
                  isSearching={isSearching}
                />
              </div>
            </div>

            {/* Table Section - Responsive Container */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200/60 overflow-hidden">
              {isMobile || isTablet ? (
                <div className="divide-y divide-slate-100">
                  {billings.map((billing) => (
                    <MobileInvoiceItem
                      key={billing.id}
                      invoice={billing}
                      onAccept={() => handleAction(billing.id, 'charges_approved')}
                      onDispute={(disputeData) => handleDisputeCharges(billing.id, disputeData)}
                      nextActions={getNextActions(billing)}
                      onRefresh={handleRefreshBilling}
                      handleAction={handleAction}
                    />
                  ))}
                </div>
              ) : (
                <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent">
                  <table className="w-full table-fixed" style={{ minWidth: '850px' }}>
                    <TableHeader
                      columns={TABLE_COLUMNS}
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                    />
                    <tbody className="bg-white">
                      <AnimatePresence>
                        {billings.map((billing) => (
                          <TableRow 
                            key={billing.id} 
                            invoice={billing}
                            user={user}
                            onAccept={() => handleAction(billing.id, 'charges_approved')}
                            onDispute={(disputeData) => handleDisputeCharges(billing.id, disputeData)}
                            nextActions={getNextActions(billing)}
                            onRefresh={handleRefreshBilling}
                            handleAction={handleAction}
                          />
                        ))}
                      </AnimatePresence>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

const DashboardCard = ({ title, value, icon: Icon, trend, trendValue, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    yellow: 'bg-amber-50 text-amber-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600'
  };

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className="bg-white rounded-xl shadow-md p-6"
    >
      <div className="flex justify-between items-start mb-4">
        <div className={`rounded-xl p-3 ${colorClasses[color]}`}>
          <Icon className="h-6 w-6" />
        </div>
        {trend && (
          <div className={`flex items-center ${
            trend === 'up' ? 'text-green-500' : 'text-red-500'
          }`}>
            {trend === 'up' ? (
              <ArrowUpRight className="h-4 w-4 mr-1" />
            ) : (
              <ArrowDownRight className="h-4 w-4 mr-1" />
            )}
            <span className="text-sm font-medium">{trendValue}</span>
          </div>
        )}
      </div>
      <h3 className="text-gray-500 text-sm mb-1">{title}</h3>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    </motion.div>
  );
};

const TableRow = ({ invoice, onAccept, onDispute, nextActions, onRefresh, handleAction }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [documents, setDocuments] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [selectedDocType, setSelectedDocType] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const user = useSelector((state) => state.auth.user);
  const isCarrier = user?.role?.startsWith('carrier');
  const [showAddChargeModal, setShowAddChargeModal] = useState(false);
  const isBroker = useSelector(selectIsBroker);
  const isCustomer = user?.role?.startsWith('customer');
  const canAddCharges = isCustomer && isBroker;

  const fetchDocuments = async () => {
    try {
      const response = await api.get(`/api/loads/${invoice.id}/documents`);
      setDocuments(response.data.documents);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  useEffect(() => {
    if (isExpanded && !documents) {
      fetchDocuments();
    }
  }, [isExpanded]);

  const documentSections = [
    {
      title: 'Load Documents',
      icon: TruckIcon,
      docs: documents?.load_documents || [],
      description: 'Required load documentation',
      types: DOCUMENT_TYPES.load
    },
    {
      title: 'Billing Documents',
      icon: Receipt,
      docs: documents?.billing_documents || [],
      description: 'Invoices and payment documentation',
      types: DOCUMENT_TYPES.billing
    },
    {
      title: 'Dispute Documents',
      icon: AlertCircle,
      docs: documents?.dispute_documents || [],
      description: 'Supporting documentation for disputes or claims',
      types: DOCUMENT_TYPES.dispute
    }
  ];

  const UploadModal = ({ onClose, invoice, onUploadComplete }) => {
    const [selectedDocType, setSelectedDocType] = useState('');
    const [entityType, setEntityType] = useState('billing');
    const [files, setFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const fileInputRef = useRef(null);

    const onDrop = useCallback((acceptedFiles) => {
      setFiles(prevFiles => [...prevFiles, ...acceptedFiles]);
    }, []);

    const removeFile = useCallback((index) => {
      setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      onDrop,
      accept: {
        'image/*': ['.jpg', '.jpeg', '.png'],
        'application/pdf': ['.pdf'],
        'application/msword': ['.doc'],
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
      },
      multiple: true,
      noClick: true,
      noKeyboard: true,
      maxSize: 10 * 1024 * 1024 // 10MB max file size
    });

    const handleAddMorePages = () => {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    };

    const handleSubmit = async () => {
      if (!files.length || !selectedDocType) {
        setUploadError('Please select both document type and files');
        return;
      }

      setIsUploading(true);
      setUploadError(null);

      try {
        // Handle multi-page upload
        if (files.length > 1) {
          const formData = new FormData();
          formData.append('entity_type', entityType);
          formData.append('document_type', selectedDocType);
          formData.append('load_id', invoice.id);
          formData.append('is_multi_page', 'true');
          formData.append('page_count', files.length.toString());
          
          // Append files with the correct key
          files.forEach((file, index) => {
            formData.append('files[]', file);
          });

          await api.post('/api/documents/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } else {
          // Single file upload - maintain backwards compatibility
          const formData = new FormData();
          formData.append('file', files[0]);
          formData.append('entity_type', entityType);
          formData.append('document_type', selectedDocType);
          formData.append('load_id', invoice.id);

          await api.post('/api/documents/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }

        toast.success('Documents uploaded successfully');
        await onUploadComplete?.(true);
        onClose();
      } catch (error) {
        console.error('Upload error:', error);
        setUploadError(
          error.response?.data?.error || 
          error.response?.data?.message || 
          'Failed to upload documents'
        );
      } finally {
        setIsUploading(false);
      }
    };

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        className="bg-white rounded-xl p-6 max-w-md w-full shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold">Upload Documents</h3>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-500"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {uploadError && (
          <div className="mb-4 rounded-md bg-red-50 p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Upload Error</h3>
                <div className="mt-2 text-sm text-red-700">{uploadError}</div>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
          {/* Entity Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Document Category
            </label>
            <select
              value={entityType}
              onChange={(e) => setEntityType(e.target.value)}
              className="block w-full px-3 py-2 rounded-xl border border-gray-300 
                shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 
                transition-shadow duration-200"
            >
              {Object.keys(DOCUMENT_TYPES).map(type => (
                <option key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)} Documents
                </option>
              ))}
            </select>
          </div>

          {/* Document Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Document Type
            </label>
            <select
              value={selectedDocType}
              onChange={(e) => setSelectedDocType(e.target.value)}
              className="block w-full px-3 py-2 rounded-xl border border-gray-300 
                shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 
                transition-shadow duration-200"
            >
              <option value="">Select document type</option>
              {DOCUMENT_TYPES[entityType]?.options.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* File Upload Area */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Upload Files
            </label>
            
            {files.length === 0 ? (
              <div
                {...getRootProps()}
                className={`
                  mt-1 flex flex-col items-center justify-center px-6 pt-5 pb-6 
                  border-2 border-dashed rounded-xl transition-colors duration-200
                  ${isDragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-300'}
                  hover:border-blue-300
                `}
              >
                <div className="space-y-2 text-center">
                  <Upload className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="flex flex-col items-center text-sm text-gray-600">
                    <button
                      type="button"
                      onClick={handleAddMorePages}
                      className="font-medium text-blue-600 hover:text-blue-500 focus:outline-none focus:underline transition duration-150 ease-in-out"
                    >
                      Click to upload
                    </button>
                    <p className="mt-1">or drag and drop</p>
                    <p className="text-xs text-gray-500 mt-2">
                      Upload multiple pages - PNG, JPG, PDF up to 10MB each
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="mt-4 grid grid-cols-2 gap-3">
                  {files.map((file, index) => {
                    const isImage = file.type.startsWith('image/');
                    const preview = isImage ? URL.createObjectURL(file) : null;
                    
                    return (
                      <div 
                        key={index} 
                        className="relative group bg-gray-50 rounded-lg p-3 border border-gray-200"
                      >
                        {isImage ? (
                          <div className="relative aspect-[3/2] w-full overflow-hidden rounded-lg bg-gray-100">
                            <img
                              src={preview}
                              alt={`Preview ${index + 1}`}
                              className="h-full w-full object-cover"
                              onLoad={() => URL.revokeObjectURL(preview)}
                            />
                          </div>
                        ) : (
                          <div className="relative aspect-[3/2] w-full overflow-hidden rounded-lg bg-gray-100 flex items-center justify-center">
                            <FileText className="w-8 h-8 text-gray-400" />
                          </div>
                        )}
                        
                        <div className="mt-2 flex items-center justify-between">
                          <span className="text-sm text-gray-600 truncate flex-1">
                            Page {index + 1}
                          </span>
                          <button
                            onClick={() => removeFile(index)}
                            className="p-1 rounded-full hover:bg-gray-200 text-gray-500 hover:text-red-500 transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                <button
                  type="button"
                  onClick={handleAddMorePages}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 
                    shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 
                    bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 
                    focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Add More Pages
                </button>
              </div>
            )}
            
            <input
              {...getInputProps()}
              ref={fileInputRef}
              style={{ display: 'none' }}
            />
          </div>
        </form>

        <div className="mt-6 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors duration-200"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isUploading || !selectedDocType || !files.length}
            className={`
              px-4 py-2 rounded-xl font-medium text-sm
              inline-flex items-center transition-all duration-200
              ${isUploading ? 'bg-blue-100 text-blue-600' : 'bg-blue-600 text-white hover:bg-blue-700'}
              disabled:opacity-50 disabled:cursor-not-allowed
            `}
          >
            {isUploading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Upload Documents
          </button>
        </div>
      </motion.div>
    );
  };

  const handleTimelineAction = (action) => {
    console.log('Timeline action received:', action);
    
    // Check if the action is an object with an "action" property equal to "dispute_charges"
    if (typeof action === 'object' && action.action === 'dispute_charges') {
      setShowDisputeModal(true);
      return;
    }
    // Check if it's a string equal to "dispute_charges"
    if (action === 'dispute_charges') {
      setShowDisputeModal(true);
      return;
    }
    if (action === 'show_upload_modal') {
      setShowUploadModal(true);
    } else if (action === 'charges_approved') {
      onAccept();
    } else if (action === 'payment_marked') {
      handleAction(invoice.id, 'payment_marked');
    } else if (action === 'payment_verified') {
      handleAction(invoice.id, 'payment_verified');
    } else {
      handleAction(invoice.id, action);
    }
  };

  const needsDisputeDocuments = () => {
    return isCarrier && 
           invoice.status === 'under_review' && 
           (!documents?.dispute_documents || documents.dispute_documents.length === 0);
  };

  const handleDisputeCharges = async (disputeData) => {
    try {
      await onDispute(disputeData);
      setShowDisputeModal(false);
    } catch (error) {
      console.error('Error in handleDisputeCharges:', error);
      throw error;
    }
  };

  const handleChargeAdded = async () => {
    setShowAddChargeModal(false);
    await onRefresh(invoice.id);
  };

  return (
    <>
      <motion.tr
        layout
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="hover:bg-slate-50/50 border-b border-slate-100 last:border-b-0 transition-colors"
      >
        {TABLE_COLUMNS.map(column => (
          <td 
            key={column.id} 
            style={{ minWidth: column.minWidth }}
            className="px-3 py-3 whitespace-nowrap"
          >
            {column.render(invoice, {
              onExpand: () => setIsExpanded(!isExpanded),
              isExpanded,
              onAddCharge: () => setShowAddChargeModal(true),
              canAddCharges
            })}
          </td>
        ))}
      </motion.tr>

      <AnimatePresence>
        {isExpanded && (
          <motion.tr
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
          >
            <td colSpan={8} className="px-4 py-4 bg-gray-50">
              <div className="max-w-full overflow-x-auto">
                {/* Only show alert for carriers */}
                {needsDisputeDocuments() && (
                  <MissingDocumentsAlert onUpload={() => setShowUploadModal(true)} />
                )}
                
                {/* Add Timeline Component */}
                <BillingTimeline
                  currentStatus={invoice.status}
                  onAction={handleTimelineAction}
                  validTransitions={nextActions}
                  statusHistory={invoice.status_history}
                />

                {/* Documents Section - Redesigned */}
                <div className="bg-white rounded-lg shadow-sm">
                  <div className="p-4">
                    <div className="flex justify-between items-center mb-6">
                      <h3 className="text-lg font-medium text-gray-900">Related Documents</h3>
                      {/* Only show Add Document button for non-customers */}
                      {!user?.role?.startsWith('customer') && (
                        <button
                          onClick={() => setShowUploadModal(true)}
                          disabled={isUploading}
                          className="px-3 py-1.5 text-sm bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 flex items-center gap-2 transition-colors"
                        >
                          {isUploading ? (
                            <span className="flex items-center gap-2">
                              <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
                              Uploading...
                            </span>
                          ) : (
                            <>
                              <Upload className="w-4 h-4" />
                              Add Document
                            </>
                          )}
                        </button>
                      )}
                    </div>

                    {/* Document Sections */}
                    <div className="space-y-6">
                      {documentSections.map(section => (
                        <div key={section.title} className="space-y-3">
                          <div className="flex items-center gap-2">
                            <section.icon className="w-4 h-4 text-gray-400" />
                            <h4 className="text-sm font-medium text-gray-900">{section.title}</h4>
                          </div>
                          {section.docs.length > 0 ? (
                            <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-4">
                              {section.docs.map(doc => (
                                <DocumentCard 
                                  key={doc.id} 
                                  document={doc}
                                  isMobile={false}
                                />
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500 italic pl-6">No documents available</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </td>
          </motion.tr>
        )}
      </AnimatePresence>

      {showUploadModal && createPortal(
        <div 
          className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowUploadModal(false);
            }
          }}
        >
          <UploadModal
            invoice={invoice}
            onClose={() => setShowUploadModal(false)}
            onUploadComplete={async () => {
              await fetchDocuments();
              await onRefresh(invoice.id);
            }}
          />
        </div>,
        document.body
      )}

      {showDisputeModal && (
        <DisputeModal
          isOpen={showDisputeModal}
          onClose={() => setShowDisputeModal(false)}
          onSubmit={handleDisputeCharges}
          invoice={{
            ...invoice,
            id: invoice.transaction_uuid || invoice.transaction_id || invoice.id
          }}
        />
      )}

      {showAddChargeModal && canAddCharges && (
        <AddChargeModal
          isOpen={showAddChargeModal}
          onClose={() => setShowAddChargeModal(false)}
          paymentId={invoice.transaction_uuid || invoice.transaction_id || invoice.id}
          onChargeAdded={handleChargeAdded}
        />
      )}
    </>
  );
};

export default Billings;